import { defineBoot } from '#q-app/wrappers'
import axios, { AxiosError, type AxiosInstance } from 'axios'
import { Cookies } from 'quasar'
import { $t } from 'boot/common/i18n'
import createAuthRefreshInterceptor from 'composables/common/import/authRefreshInterceptor'

import { useAuthStore } from 'stores/receiver/account/auth/auth'
import { useErrorStore } from 'stores/common/error'
import { useCommonAuthStore } from 'stores/common/rulesEditor/commonAuth'

import { useAuthInterceptor } from 'composables/common/auth/useAuthInterceptor'

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)

let api: AxiosInstance
let unAuthApi: AxiosInstance

export default defineBoot(({ app, redirect, router, ssrContext, store }) => {
  api = axios.create({
    baseURL: process.env.API_BASE_URL,
  })

  unAuthApi = axios.create({
    baseURL: process.env.API_BASE_URL,
  })

  const { addLocale, responseInterceptor, responseErrorInterceptor } =
    useAuthInterceptor()

  // UN-AUTH API
  unAuthApi.interceptors.request.use((config) => addLocale(config, app))

  unAuthApi.interceptors.response.use(
    (res) => responseInterceptor(res, store),
    (e) => responseErrorInterceptor(e, store)
  )

  // AUTH API
  api.interceptors.request.use((config) => {
    config = addLocale(config, app)

    const cookies = process.env.SERVER ? Cookies.parseSSR(ssrContext) : Cookies // otherwise we're on client
    const access = cookies.get('access_token')

    if (access && config.headers) {
      config.headers.Authorization = `Bearer ${access}`
    }

    return config
  })

  // Refresh Interceptor
  createAuthRefreshInterceptor(api, async (failedRequest) => {
    const authStore = useAuthStore(store)

    const res = await authStore.refreshTokens(ssrContext)

    // Continue the call if we get the refresh token
    if (res) {
      return Promise.resolve()
    } else {
      const { logoutClean } = useCommonAuthStore()
      logoutClean()

      if (process.env.SERVER) {
        redirect('/login')
      } else {
        void router.push({
          name: 'login',
        })
      }

      // Add an error
      useErrorStore().backErrors = {
        generalError: $t('error.auth.refresh'),
      }

      const error = new AxiosError(
        'failed to refresh token',
        failedRequest.response.statusText,
        failedRequest.response.config,
        failedRequest.request,
        {
          ...failedRequest.response,
          status: 403,
          path: failedRequest.response.config.url,
          message: $t('error.auth.refresh'),
        }
      )
      return Promise.reject(error)
    }
  })

  // MAIN RESPONSE HANDLING
  api.interceptors.response.use(
    (config) => responseInterceptor(config, store),
    (e) => responseErrorInterceptor(e, store)
  )
})

export { api, unAuthApi }
